'use client';

import Link from "next/link";
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useSession } from '@/stores/session';
import { User } from '@/lib/types/api';

interface MobileTabBarProps {
  locale: string;
  user?: User | null; // Optional - artık kullanılmıyor, sadece geriye dönük uyumluluk için
  isAuthenticated?: boolean; // Optional - artık kullanılmıyor, sadece geriye dönük uyumluluk için
  translations: {
    home: string;
    auctions: string;
    requestForm: string;
    profile: string;
    dashboard: string;
  };
}

export default function MobileTabBar({ 
  locale, 
  translations 
}: MobileTabBarProps) {
  const pathname = usePathname();
  const { user } = useSession(); // Sadece client-side session'dan user bilgisini al
  
  // Hydration için güvenli state
  const [mounted, setMounted] = useState(false);
  const [currentPathname, setCurrentPathname] = useState('');

  useEffect(() => {
    setMounted(true);
    setCurrentPathname(pathname);
  }, [pathname]);

  // Sadece client-side session'dan gelen user'ı kullan
  const isAuthenticated = !!user;
  
  // Admin kontrolü
  const isAdmin = user?.roles?.includes('Admin');

  // Aktif sayfayı tespit et
  const isActive = (path: string) => {
    if (!mounted) return false;
    
    if (path === `/${locale}`) {
      return currentPathname === `/${locale}` || currentPathname === `/${locale}/`;
    }
    return currentPathname.startsWith(path);
  };

  // Tab bar sadece mobile cihazlarda görünsün
  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
      <nav className="flex items-center justify-around px-2 py-1">
        {/* Ana Sayfa */}
        <Link 
          href={`/${locale}`}
          prefetch={false}
          className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors min-w-0 flex-1 ${
            isActive(`/${locale}`) 
              ? 'text-red-600 bg-red-50' 
              : 'text-gray-600 hover:text-red-600'
          }`}
        >
          <svg className="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span className="text-xs font-medium truncate">{translations.home}</span>
        </Link>

        {/* Müzayedeler */}
        <Link 
          href={`/${locale}/muzayedeler`}
          prefetch={false}
          className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors min-w-0 flex-1 ${
            isActive(`/${locale}/muzayedeler`) 
              ? 'text-red-600 bg-red-50' 
              : 'text-gray-600 hover:text-red-600'
          }`}
        >
          <svg className="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          <span className="text-xs font-medium truncate">{translations.auctions}</span>
        </Link>

        {/* Talep Formu */}
        <Link 
          href={`/${locale}/talep-formu`}
          prefetch={false}
          className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors min-w-0 flex-1 ${
            isActive(`/${locale}/talep-formu`) 
              ? 'text-red-600 bg-red-50' 
              : 'text-gray-600 hover:text-red-600'
          }`}
        >
          <svg className="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-xs font-medium truncate">{translations.requestForm}</span>
        </Link>

        {/* Kullanıcı Menüsü */}
        {isAuthenticated && user ? (
          // Giriş yapmış kullanıcı
          <>
            {mounted && isAdmin ? (
              // Admin Dashboard
              <Link 
                href="/yonetim-paneli"
                prefetch={false}
                className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors min-w-0 flex-1 ${
                  isActive('/yonetim-paneli') 
                    ? 'text-red-600 bg-red-50' 
                    : 'text-gray-600 hover:text-red-600'
                }`}
              >
                <svg className="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                <span className="text-xs font-medium truncate">{translations.dashboard}</span>
              </Link>
            ) : (
              // Normal kullanıcı profil
              <Link 
                href={`/${locale}/hesabim`}
                prefetch={false}
                className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors min-w-0 flex-1 ${
                  isActive(`/${locale}/hesabim`) 
                    ? 'text-red-600 bg-red-50' 
                    : 'text-gray-600 hover:text-red-600'
                }`}
              >
                <svg className="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-xs font-medium truncate">{translations.profile}</span>
              </Link>
            )}
          </>
        ) : (
          // Giriş yapmamış kullanıcı - Giriş butonu
          <Link 
            href={`/${locale}/giris`}
            prefetch={false}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors min-w-0 flex-1 ${
              isActive(`/${locale}/giris`) 
                ? 'text-red-600 bg-red-50' 
                : 'text-gray-600 hover:text-red-600'
            }`}
          >
            <svg className="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
            <span className="text-xs font-medium truncate">Giriş</span>
          </Link>
        )}
      </nav>
    </div>
  );
}
